import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Node, mergeAttributes } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import { TextStyle, Color, FontFamily, FontSize } from "@tiptap/extension-text-style";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading2, Heading3,
  List, ListOrdered, Quote, Link2, Unlink, Image as ImageIcon, Minus, Video,
  AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Highlighter, Baseline, Eraser,
} from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";

// Convierte un enlace público (YouTube / Vimeo / Dailymotion) a su URL embebible.
function toEmbedUrl(url) {
  const u = (url || "").trim();
  let m = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = u.match(/vimeo\.com\/(?:video\/|channels\/[\w]+\/|groups\/[\w]+\/videos\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  m = u.match(/dailymotion\.com\/(?:video|embed\/video)\/([\w]+)/);
  if (m) return `https://www.dailymotion.com/embed/video/${m[1]}`;
  m = u.match(/dai\.ly\/([\w]+)/);
  if (m) return `https://www.dailymotion.com/embed/video/${m[1]}`;
  return u; // se asume que ya es una URL embebible
}

// Nodo de video responsivo. Se renderiza igual en el editor y en el artículo público.
const VideoEmbed = Node.create({
  name: "videoEmbed",
  group: "block",
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (el) => el.getAttribute("src"),
        renderHTML: (attrs) => (attrs.src ? { src: attrs.src } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "iframe[data-video]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      { class: "video-embed" },
      ["iframe", mergeAttributes(HTMLAttributes, {
        "data-video": "true", frameborder: "0", loading: "lazy",
        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
        allowfullscreen: "true",
      })],
    ];
  },
  addCommands() {
    return {
      setVideoEmbed: (options) => ({ commands }) => commands.insertContent({ type: this.name, attrs: options }),
    };
  },
});

const FONTS = [
  { label: "Sans (predeterminada)", value: "" },
  { label: "Serif editorial (Playfair)", value: "'Playfair Display', serif" },
];
const SIZES = [
  { label: "Normal", value: "" },
  { label: "Pequeño", value: "0.9rem" },
  { label: "Grande", value: "1.35rem" },
  { label: "Muy grande", value: "1.8rem" },
];

function Divider() {
  return <span className="w-px h-6 bg-sunset-border mx-1 self-center" />;
}

function ToolBtn({ onClick, active, title, children }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
      className={`p-2 rounded-sm transition-colors ${active ? "bg-sunset-accent text-white" : "text-sunset-ink hover:bg-sunset-border/60"}`}>
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } },
      }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Image.configure({ inline: false, HTMLAttributes: { class: "editor-image" } }),
      VideoEmbed,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "article-body tiptap-surface focus:outline-none min-h-[360px] px-6 py-5",
        "data-testid": "rich-editor",
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sincroniza cambios externos (ej. carga async del artículo) sin romper el cursor al escribir.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (!editor.isFocused && (value || "") !== current) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL del enlace:", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addVideo = () => {
    const url = window.prompt("Pega el enlace del video (YouTube, Vimeo, Dailymotion o URL embed):");
    if (!url) return;
    const embed = toEmbedUrl(url);
    editor.chain().focus().setVideoEmbed({ src: embed }).run();
    toast.success("Video insertado");
  };

  const uploadImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().setImage({ src: mediaUrl(data.url) }).run();
      toast.success("Imagen insertada");
    } catch { toast.error("Error al subir"); }
    e.target.value = "";
  };

  return (
    <div className="border border-sunset-border bg-white">
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-sunset-border bg-sunset-bg sticky top-0 z-10">
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Deshacer"><Undo2 size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Rehacer"><Redo2 size={16} /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrita"><Bold size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Cursiva"><Italic size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Subrayado"><UnderlineIcon size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Tachado"><Strikethrough size={16} /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Título 2"><Heading2 size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Título 3"><Heading3 size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Cita"><Quote size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista"><List size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada"><ListOrdered size={16} /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Izquierda"><AlignLeft size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centrar"><AlignCenter size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Derecha"><AlignRight size={16} /></ToolBtn>
        <Divider />
        <select data-testid="rich-font" title="Tipografía" onChange={(e) => { const v = e.target.value; v ? editor.chain().focus().setFontFamily(v).run() : editor.chain().focus().unsetFontFamily().run(); }}
          className="text-xs border border-sunset-border bg-white px-2 py-1.5 rounded-sm text-sunset-ink cursor-pointer">
          {FONTS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <select data-testid="rich-size" title="Tamaño" onChange={(e) => { const v = e.target.value; v ? editor.chain().focus().setFontSize(v).run() : editor.chain().focus().unsetFontSize().run(); }}
          className="text-xs border border-sunset-border bg-white px-2 py-1.5 rounded-sm text-sunset-ink cursor-pointer">
          {SIZES.map((s) => <option key={s.label} value={s.value}>{s.label}</option>)}
        </select>
        <label className="p-2 rounded-sm hover:bg-sunset-border/60 cursor-pointer text-sunset-ink flex items-center" title="Color de texto">
          <Baseline size={16} />
          <input type="color" data-testid="rich-color" onInput={(e) => editor.chain().focus().setColor(e.target.value).run()} className="w-0 h-0 opacity-0 absolute" />
        </label>
        <label className="p-2 rounded-sm hover:bg-sunset-border/60 cursor-pointer text-sunset-ink flex items-center" title="Resaltar">
          <Highlighter size={16} />
          <input type="color" data-testid="rich-highlight" onInput={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()} className="w-0 h-0 opacity-0 absolute" />
        </label>
        <ToolBtn onClick={() => editor.chain().focus().unsetColor().unsetHighlight().unsetFontFamily().unsetFontSize().run()} title="Quitar estilos de texto"><Eraser size={16} /></ToolBtn>
        <Divider />
        <ToolBtn onClick={addLink} active={editor.isActive("link")} title="Enlace"><Link2 size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().unsetLink().run()} title="Quitar enlace"><Unlink size={16} /></ToolBtn>
        <label className="p-2 rounded-sm hover:bg-sunset-border/60 cursor-pointer text-sunset-ink flex items-center" title="Insertar imagen">
          <ImageIcon size={16} />
          <input type="file" accept="image/*" onChange={uploadImg} className="hidden" data-testid="rich-image" />
        </label>
        <ToolBtn onClick={addVideo} title="Insertar video"><Video size={16} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador"><Minus size={16} /></ToolBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
